const REFERRAL_CODE_STORAGE_KEY = 'referralCode';
const REFERRAL_ATTRIBUTION_KEY = 'referralAttribution:v1';
const REFERRAL_MAX_AGE = 30 * 24 * 60 * 60 * 1000;

export const normalizeReferralCode = (code: string) => {
  const normalized = code.trim().toUpperCase();
  return /^[A-Z0-9]{6}$/.test(normalized) ? normalized : null;
};

export const getStoredReferralCode = () => {
  try {
    const stored = localStorage.getItem(REFERRAL_ATTRIBUTION_KEY);
    if (stored) {
      const { code, expiresAt } = JSON.parse(stored);
      if (typeof code === 'string' && expiresAt > Date.now()) {
        return normalizeReferralCode(code);
      }
      clearStoredReferralCode();
      return null;
    }
  } catch {
    // Browser storage may be unavailable; retain same-tab attribution if possible.
  }
  try {
    return normalizeReferralCode(sessionStorage.getItem(REFERRAL_CODE_STORAGE_KEY) || '');
  } catch {
    return null;
  }
};

export const setStoredReferralCode = (code: string) => {
  const normalizedCode = normalizeReferralCode(code);
  if (!normalizedCode) return;
  try {
    localStorage.setItem(REFERRAL_ATTRIBUTION_KEY, JSON.stringify({
      code: normalizedCode,
      expiresAt: Date.now() + REFERRAL_MAX_AGE,
    }));
  } catch { /* Fall back to session storage. */ }
  try {
    sessionStorage.setItem(REFERRAL_CODE_STORAGE_KEY, normalizedCode);
  } catch { /* Signup must remain available when storage is blocked. */ }
};

export const clearStoredReferralCode = () => {
  try { localStorage.removeItem(REFERRAL_ATTRIBUTION_KEY); } catch { /* Storage blocked. */ }
  try { sessionStorage.removeItem(REFERRAL_CODE_STORAGE_KEY); } catch { /* Storage blocked. */ }
};

const DEFAULT_REFERRAL_TNC_URL = 'https://locumspace.co/referral-terms';

export const buildReferralShareMessage = (referralLink?: string | null) => {
  const trimmedLink = referralLink?.trim();
  if (!trimmedLink) {
    return `Hi! I've been using Locumspace to find flexible healthcare shifts and job opportunities. Once my referral link is ready, I'll share it with you so you can sign up and get started.`;
  }

  return `I'm on Locumspace, a platform connecting healthcare professionals to great opportunities. Sign up with my referral link:\n\n${trimmedLink}`;
};

export const getReferralTermsUrl = () => {
  return process.env.REACT_APP_REFERRAL_TNC_URL || DEFAULT_REFERRAL_TNC_URL;
};

export const maskReferralEmail = (email?: string | null) => {
  if (!email) return 'Unavailable';
  const [name, domain] = email.split('@');
  if (!domain) return email;

  const visible = name.slice(-2);
  const hidden = '*'.repeat(Math.max(name.length - visible.length, 2));
  return `${hidden}${visible}@${domain}`;
};

export const isTierThreeUser = (tier?: string | null) => {
  return tier?.trim().toUpperCase() === 'TIER 3';
};
